import type {
  ReactElement,
  ReactNode,
  FunctionComponent,
  SVGProps,
  HTMLProps,
  RefObject,
} from 'react';
import { useState, forwardRef } from 'react';

import { ApiRoute } from 'config';
import { del } from 'utils/network';

import { Component as SuccessIcon } from 'icons/icon-circle--success.svg';
import { Component as WarningIcon } from 'icons/icon-circle--warning.svg';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';

enum BannerType {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
}

type ThemedProps = {
  button?: HTMLProps<HTMLButtonElement | HTMLLinkElement>;
  isCentered?: boolean;
  id?: number;
  children: ReactNode;
}

type Props = ThemedProps & {
  icon?: FunctionComponent<SVGProps<SVGElement>>;
  type?: BannerType;
};

const Banner = forwardRef(function Banner({
  icon: Icon,
  button,
  isCentered = false,
  type = BannerType.INFO,
  id,
  children,
}: Props, ref: RefObject<HTMLDivElement>): ReactElement | null {
  const [isClosed, setIsClosed] = useState(false);

  if (isClosed) return null;

  return (
    <div
      ref={ref}
      className={[
        'c-alert',
        `c-alert--${type}`,
        isCentered ? 'c-alert--center' : '',
      ].join(' ')}
    >
      {Icon && <Icon height="28" width="28" />}

      <div className="c-alert__text ml-1">
        {children}
      </div>

      {button}

      {id && (
        <button
          className="c-alert__close c-link"
          type="button"
          aria-label="Close promotional banner"
          onClick={() => {
            del(`${ApiRoute.NOTIFICATIONS}/${id}`);
            setIsClosed(true);
          }}
        >
          <CloseIcon width="24" height="24" />
        </button>
      )}
    </div>
  );
});

const SuccessBanner = forwardRef(function SuccessBanner({
  button,
  isCentered = false,
  id,
  children,
}: ThemedProps, ref: RefObject<HTMLDivElement>): ReactElement | null {
  return (
    <Banner
      icon={SuccessIcon}
      type={BannerType.SUCCESS}
      button={button}
      isCentered={isCentered}
      id={id}
      ref={ref}
    >
      {children}
    </Banner>
  );
});

const WarningBanner = forwardRef(function WarningBanner({
  button,
  isCentered = false,
  id,
  children,
}: ThemedProps, ref: RefObject<HTMLDivElement>): ReactElement | null {
  return (
    <Banner
      icon={WarningIcon}
      type={BannerType.INFO}
      button={button}
      isCentered={isCentered}
      id={id}
      ref={ref}
    >
      {children}
    </Banner>
  );
});

type ExtendedBanner = typeof Banner & {
  Warning: typeof WarningBanner;
  Success: typeof SuccessBanner;
};

(Banner as ExtendedBanner).Warning = WarningBanner;
(Banner as ExtendedBanner).Success = SuccessBanner;

export default Banner as ExtendedBanner;
